import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import useChartTheme from './useChartTheme';
import ChartTooltip from './ChartTooltip';
import styles from './Charts.module.scss';

/* ────────────────────────────────────────────────────────────
 * BarChart — themed Recharts wrapper.
 *
 *   <BarChart
 *     data={[{ day: 'Mon', orders: 24, refunds: 2 }, ...]}
 *     xKey="day"
 *     series={[
 *       { key: 'orders',  name: 'Orders' },
 *       { key: 'refunds', name: 'Refunds' },
 *     ]}
 *     height={320}
 *     valueFormatter={(v) => v.toLocaleString()}
 *   />
 *
 * Series colors auto-cycle through --chart-1 → --chart-8.
 * Override per series via `color: 'var(--chart-3)'` on the
 * series entry.
 *
 * Animates in on mount per MOTION.md §4.10 (400ms ease-out-quart).
 * ──────────────────────────────────────────────────────────── */

const BarChart = ({
  data,
  xKey,
  series,
  height,
  stacked,
  layout,
  valueFormatter,
  labelFormatter,
  showLegend,
  showGrid,
  showTooltip,
  className,
  ariaLabel,
}) => {
  const palette = useChartTheme();

  return (
    <div
      className={[styles.chart, className || ''].filter(Boolean).join(' ')}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart
          data={data}
          layout={layout}
          margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.grid}
              vertical={layout === 'vertical'}
              horizontal={layout !== 'vertical'}
            />
          )}
          {layout === 'vertical' ? (
            <>
              <XAxis type="number" stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey={xKey} stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
            </>
          )}
          {showTooltip && (
            <Tooltip
              cursor={{ fill: palette.grid }}
              content={(p) => (
                <ChartTooltip
                  {...p}
                  valueFormatter={valueFormatter}
                  labelFormatter={labelFormatter}
                />
              )}
            />
          )}
          {showLegend && <Legend wrapperStyle={{ color: palette.axis, fontSize: 12 }} />}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name || s.key}
              fill={s.color || palette.series[i % palette.series.length]}
              radius={[4, 4, 0, 0]}
              stackId={stacked ? 'stack' : undefined}
              isAnimationActive
              animationDuration={400}
            />
          ))}
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xKey: PropTypes.string.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stacked: PropTypes.bool,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  valueFormatter: PropTypes.func,
  labelFormatter: PropTypes.func,
  showLegend: PropTypes.bool,
  showGrid: PropTypes.bool,
  showTooltip: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

BarChart.defaultProps = {
  height: 320,
  stacked: false,
  layout: 'horizontal',
  valueFormatter: null,
  labelFormatter: null,
  showLegend: true,
  showGrid: true,
  showTooltip: true,
  className: '',
  ariaLabel: 'Bar chart',
};

export default BarChart;
